import { useQuery } from '@tanstack/react-query'
import { History } from 'lucide-react'
import type { ReactNode } from 'react'

import type { PriceChangeSummary } from '@aeris/contracts'
import { UNIT_PRICE_SCALE, formatMoney, fromString } from '@aeris/money'
import {
  Badge,
  Dialog,
  DialogContent,
  EmptyState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchPriceHistory } from '../lib/api.ts'

/**
 * O histórico de preço e custo de um produto (F125).
 *
 * A pergunta que a loja faz quando uma venda sai com o preço errado é uma só:
 * quem mudou, quando, de quanto para quanto — e quanto sobrava naquele momento.
 * Cada linha responde as quatro. A margem é a DE ENTÃO, fotografada quando a
 * mudança aconteceu: o custo pode ter mudado depois, e a margem de hoje não
 * explica a decisão de ontem.
 *
 * Molde do `stock-history-dialog.tsx`, pelo mesmo motivo: um livro-razão lido
 * por uma pessoa, nunca apagado.
 *
 * A tabela é exportada sozinha (F131) para a aba "Histórico de preço" da ficha
 * mostrar a mesma coisa sem abrir uma janela por cima da outra.
 */

const KIND_LABELS: Record<PriceChangeSummary['kind'], string> = {
  sale_price: 'Preço de venda',
  cost: 'Custo',
}

const SOURCE_LABELS: Record<PriceChangeSummary['source'], string> = {
  'product-form': 'Cadastro do produto',
  'price-list': 'Tabela de preços',
  // O custo que nenhuma pessoa digitou: a média recalculada por uma entrada.
  purchase: 'Compra recebida',
  import: 'Importação',
}

const dateFormat = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
})

/** Dinheiro sempre por `@aeris/money`, nunca `Number()` — ADR-0016. */
const money = (value: string | null): string =>
  value === null ? '—' : formatMoney(fromString(value, UNIT_PRICE_SCALE))

/** `89.470000` vira `89,47%`. */
const percent = (value: string | null): string => {
  if (value === null) return '—'
  const [whole, fraction = ''] = value.split('.')
  return `${whole ?? '0'},${fraction.padEnd(2, '0').slice(0, 2)}%`
}

export const PriceHistoryTable = ({
  productId,
}: {
  productId: string
}): ReactNode => {
  const query = useQuery({
    queryKey: ['price-history', productId],
    queryFn: ({ signal }) => fetchPriceHistory(productId, signal),
  })

  const items = query.data?.items ?? []

  return (
    <div className="flex flex-col gap-3">
      <p className="text-(length:--text-xs) text-(--text-secondary)">
        Cada mudança de preço de venda ou de custo, com a margem (markup sobre o
        custo) que ela representava naquele momento. Nunca é apagado.
      </p>

      {query.isPending ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : query.isError ? (
        <EmptyState
          icon={<History aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar o histórico"
          description="Verifique a conexão com o servidor e tente novamente."
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<History aria-hidden="true" className="h-8 w-8" />}
          title="Nenhuma mudança registrada"
          description="O histórico começa na primeira vez que o preço ou o custo deste produto mudar."
        />
      ) : (
        <TableContainer className="max-h-[60vh] overflow-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Quando</TableHeaderCell>
                <TableHeaderCell>O quê</TableHeaderCell>
                <TableHeaderCell numeric>De</TableHeaderCell>
                <TableHeaderCell numeric>Para</TableHeaderCell>
                <TableHeaderCell numeric>Custo então</TableHeaderCell>
                <TableHeaderCell numeric>Margem então</TableHeaderCell>
                <TableHeaderCell>Quem</TableHeaderCell>
                <TableHeaderCell>Origem</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((change) => (
                <TableRow key={change.id}>
                  <TableCell className="whitespace-nowrap text-(--text-secondary)">
                    {dateFormat.format(new Date(change.occurredAt))}
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5">
                      <Badge tone={change.kind === 'cost' ? 'neutral' : 'info'}>
                        {KIND_LABELS[change.kind]}
                      </Badge>
                      {change.priceListName === null ? null : (
                        <span className="text-(length:--text-xs) text-(--text-muted)">
                          {change.priceListName}
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell numeric className="text-(--text-secondary)">
                    {money(change.previousAmount)}
                  </TableCell>
                  <TableCell numeric>{money(change.newAmount)}</TableCell>
                  <TableCell numeric className="text-(--text-secondary)">
                    {money(change.costAtChange)}
                  </TableCell>
                  <TableCell numeric>{percent(change.marginPercent)}</TableCell>
                  <TableCell className="text-(--text-secondary)">
                    {change.actorName ?? '—'}
                  </TableCell>
                  <TableCell className="text-(--text-secondary)">
                    {SOURCE_LABELS[change.source]}
                    {change.note === null ? null : (
                      <span className="block text-(length:--text-xs) text-(--text-muted)">
                        {change.note}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  )
}

export type PriceHistoryDialogProps = {
  productId: string
  productName: string
  onClose: () => void
}

export const PriceHistoryDialog = ({
  productId,
  productName,
  onClose,
}: PriceHistoryDialogProps): ReactNode => (
  <Dialog
    open
    onOpenChange={(next) => {
      if (!next) onClose()
    }}
  >
    <DialogContent
      title="Histórico de preço e custo"
      description={productName}
      className="max-w-5xl"
    >
      <PriceHistoryTable productId={productId} />
    </DialogContent>
  </Dialog>
)
